import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import SalesForceAPI from '../../../API/SalesForceAPI';
import AdminSalesForceAuditTableHeader from './AdminSalesForceAuditTableHeader';
import LinearProgress from '../../Utils/LinearProgress';
import SnackbarAlert from '../../Utils/SnackbarAlert';
import { STARTING_TABLE_PAGE, DEFAULT_ROWS_PER_PAGE, NO_TABLE_DATA_MESSAGE, ADMIN_GROUP, AUDIT_BMSF, AUDIT_SFBM } from '../../../Utils/Constants';
import { handleServerError, handleSpecialCharacters } from '../../../Utils/Helper';

const styles = theme => ({
  paperRoot: {
    width: '100%',
    backgroundColor: '#F6F6F6',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  tblBorder: {
    border: '2px solid #ffffff',
    fontSize: '12px'
  },
  jsoncol: {
    maxWidth: '550px',
    wordBreak: 'break-word',
  },
  textCenter: {
    textAlign: 'center',
    wordBreak: 'break-word',
  },
  tblRow: {
    backgroundColor: '#F2F2F2',
    '&:hover': {
      backgroundColor: theme.palette.primary.light
    },
    '&:nth-of-type(odd)': {
      backgroundColor: '#E1E1E1',
      '&:hover': {
        backgroundColor: theme.palette.primary.light
      },
    },
    height: '30px'
  },
  button: {
    margin: theme.spacing.unit
  },
  buttonSize: {
    width: '36px',
    height: '36px'
  },
  divToolbar: {
    paddingLeft: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '52px'
  },
  padRight: {
    paddingRight: '12px',
  },
  searchArea: {
    display: 'flex',
    alignItems: 'inherit'
  },
  formControl: {
    minWidth: '150px'
  },
  searchField: {
    width: '240px',
    margin: '16px 8px 0px 0px'
  },
  searchIconMargin: {
    marginRight: '4px'
  },
  tblFooter: {
    height: '30px',
    minHeight: '30px'
  },
  pageButton: {
    width: '30px',
    height: '30px',
    margin: '0px 8px'
  },
  activeWidth: {
    width: '88px'
  },
});


class AdminSalesForceAuditTable extends React.Component {
  _isMounted;
  searchTimer = null;
  salesForceAPI;

  constructor(props) {
    super(props);
    this.state = {
      data: [],
      page: STARTING_TABLE_PAGE,
      rowsPerPage: DEFAULT_ROWS_PER_PAGE,
      count: 0,
      loading: false,
      reloading: false,
      openError: false,
      alertMessage: '',
      openDialog: false,
    };
    this.salesForceAPI = new SalesForceAPI();
  }

  componentDidMount() {
    this._isMounted = true;
    this.reloadTable();
  };

  componentWillUnmount() {
    this._isMounted = false;
  }

  reloadTable() {
    this.setState({ reloading: true });
    this.props.tableList.indexOf(AUDIT_BMSF) > -1 ?
        this.salesForceAPI.getAuditBMSF(STARTING_TABLE_PAGE, this.state.rowsPerPage, this.state.search).then((response) => {
            if (this._isMounted)
                this.setState({
                    data: response.data.ResultList,
                    count: response.data.NumberFilteredResults,
                    page: STARTING_TABLE_PAGE,
                    reloading: false
                });
        }).catch((error) => {
            if (this._isMounted)
                this.setState({
                    reloading: false,
                    openError: true,
                    alertMessage: handleServerError(error)
                })
        })
        :
        this.salesForceAPI.getAuditSFBM(STARTING_TABLE_PAGE, this.state.rowsPerPage, this.state.search).then((response) => {
            if (this._isMounted)
                this.setState({
                    data: response.data.ResultList,
                    count: response.data.NumberFilteredResults,
                    page: STARTING_TABLE_PAGE,
                    reloading: false
                });
        }).catch((error) => {
            if (this._isMounted)
                this.setState({
                    reloading: false,
                    openError: true,
                    alertMessage: handleServerError(error)
                })
        });
  };

  reloadTableBySearch(searchBy) {
    this.setState({ reloading: true });
    clearTimeout(this.searchTimer);

    this.props.tableList.indexOf(AUDIT_BMSF) > -1 ?
        this.searchTimer = setTimeout(() => {
            this.salesForceAPI.getAuditBMSF(STARTING_TABLE_PAGE, this.state.rowsPerPage, this.state.search).then((response) => {
            if (this._isMounted)
                this.setState({
                data: response.data.ResultList,
                count: response.data.NumberFilteredResults,
                search: searchBy,
                page: STARTING_TABLE_PAGE,
                reloading: false
                });
            }).catch((error) => {
            if (this._isMounted)
                this.setState({
                reloading: false,
                openError: true,
                alertMessage: handleServerError(error)
                })
            });
        }, 1500)
    :
        this.searchTimer = setTimeout(() => {
            this.salesForceAPI.getAuditSFBM(STARTING_TABLE_PAGE, this.state.rowsPerPage, this.state.search).then((response) => {
            if (this._isMounted)
                this.setState({
                data: response.data.ResultList,
                count: response.data.NumberFilteredResults,
                search: searchBy,
                page: STARTING_TABLE_PAGE,
                reloading: false
                });
            }).catch((error) => {
            if (this._isMounted)
                this.setState({
                reloading: false,
                openError: true,
                alertMessage: handleServerError(error)
                })
            });
        }, 1500);
  };

  handleChangePage = (event, page) => {
    this.setState({ reloading: true });
    this.props.tableList.indexOf(AUDIT_BMSF) > -1 ?
        this.salesForceAPI.getAuditBMSF(page, this.state.rowsPerPage, this.state.search).then((response) => {
            if (this._isMounted)
                this.setState({
                    data: response.data.ResultList,
                    count: response.data.NumberFilteredResults,
                    page: page,
                    reloading: false
                });
        }).catch((error) => {
            if (this._isMounted)
                this.setState({
                    reloading: false,
                    openError: true,
                    alertMessage: handleServerError(error)
                })
        })
        :
        this.salesForceAPI.getAuditSFBM(page, this.state.rowsPerPage, this.state.search).then((response) => {
            if (this._isMounted)
                this.setState({
                    data: response.data.ResultList,
                    count: response.data.NumberFilteredResults,
                    page: page,
                    reloading: false
                });
        }).catch((error) => {
            if (this._isMounted)
                this.setState({
                    reloading: false,
                    openError: true,
                    alertMessage: handleServerError(error)
                })
        });
  };

  handleChangeRowsPerPage = event => {
    this.setState({ reloading: true });
    this.props.tableList.indexOf(AUDIT_BMSF) > -1 ?
    this.salesForceAPI.getAuditBMSF(this.state.page, event.target.value, this.state.search).then((response) => {
        if (this._isMounted)
            this.setState({
                data: response.data.ResultList,
                count: response.data.NumberFilteredResults,
                rowsPerPage: event.target.value,
                reloading: false
            });
    }).catch((error) => {
        if (this._isMounted)
            this.setState({
                reloading: false,
                openError: true,
                alertMessage: handleServerError(error)
            })
    })
    :
    this.salesForceAPI.getAuditSFBM(this.state.page, event.target.value, this.state.search).then((response) => {
        if (this._isMounted)
            this.setState({
                data: response.data.ResultList,
                count: response.data.NumberFilteredResults,
                rowsPerPage: event.target.value,
                reloading: false
            });
    }).catch((error) => {
        if (this._isMounted)
            this.setState({
                reloading: false,
                openError: true,
                alertMessage: handleServerError(error)
            })
    });
  };

  handleSearch = event => {
    this.setState({ search: handleSpecialCharacters(event.target.value) });
    this.reloadTableBySearch(handleSpecialCharacters(event.target.value));
  };

  handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    this.setState({
      openError: false,
      alertMessage: ''
    });
  };

  renderTableRecords(componentDataList) {
    const { classes } = this.props;

    const componentData = componentDataList.map(componentItem => {
      return (
        <TableRow key={componentItem.ID} tabIndex={-1} className={classes.tblRow}>
            {this.props.tableList.indexOf(AUDIT_SFBM) > -1 ? 
                <TableCell className={classNames(classes.tblBorder, classes.textCenter)} component='th' scope='row' padding='none'>
                    {componentItem.ServiceName}
                </TableCell>
            : null}
          <TableCell className={classNames(classes.tblBorder, classes.textCenter)} component='th' scope='row' padding='none'>
            {componentItem.RequestDate}
          </TableCell>
          <TableCell className={classNames(classes.tblBorder, this.props.tableList.indexOf(AUDIT_BMSF) > -1 ? classes.textCenter : classes.jsoncol)} component='th' scope='row' padding='none'>
            {componentItem.Request}
          </TableCell>
          <TableCell className={classNames(classes.tblBorder, this.props.tableList.indexOf(AUDIT_SFBM) > -1 ? classes.jsoncol : classes.textCenter)} component='th' scope='row' padding='none'>
            {componentItem.Response}
          </TableCell>
        </TableRow>
      );
    })
    return componentData;
  }

  renderNoRecordMessages() {
    return NO_TABLE_DATA_MESSAGE;
  }

  renderToolbar() {
    const { classes } = this.props;
    return (
      <div className={classNames(classes.divToolbar, this.props.user.UserRoleType.indexOf(ADMIN_GROUP) < 0 ? classes.padRight : '')}>

        <Typography variant='title' id='tableTitle'>
          {this.props.titleTableList}
        </Typography>

        <div className={classes.searchArea}>
          <FormControl className={classes.formControl}>
            <Grid container spacing={8} alignItems='flex-end'>
              <Grid item>
                <TextField
                  id='searchBy'
                  label=''
                  placeholder='Procurar'
                  type='search'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start' className={classes.searchIconMargin}>
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  className={classes.searchField}
                  value={this.state.search}
                  onChange={this.handleSearch}
                />
              </Grid>
            </Grid>
          </FormControl>
        </div>
      </div >
    );
  };

  render() {
    const { classes } = this.props;
    const { data, rowsPerPage, page, count, reloading } = this.state;

    return (
      <Paper className={classes.paperRoot}>

        {this.renderToolbar()}

        <div className={classes.tableWrapper}>
          <Table>
            <AdminSalesForceAuditTableHeader tableList={this.props.tableList}/>
            <TableBody>
              {this.renderTableRecords(data)}
            </TableBody>
          </Table>
          {(!data.length) ? this.renderNoRecordMessages() : null}
        </div>
        {reloading && <LinearProgress />}
        <TablePagination
          component='div'
          count={count}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={'Resultados por página:'}
          page={page}
          backIconButtonProps={{ className: classes.pageButton }}
          nextIconButtonProps={{ className: classes.pageButton }}
          onChangePage={this.handleChangePage}
          onChangeRowsPerPage={this.handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => `A mostrar resultados ${from}-${to} de ${count}`}
          classes={{ toolbar: classes.tblFooter }}
        />

        <SnackbarAlert openError={this.state.openError} alertMessage={this.state.alertMessage} handleCloseSnackbar={this.handleCloseSnackbar} />

      </Paper>
    );
  }
}

AdminSalesForceAuditTable.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  ...state,
});

export default connect(mapStateToProps)(withStyles(styles)(AdminSalesForceAuditTable));


// WEBPACK FOOTER //
// ./src/components/Administration/AdminSalesForceAudit/AdminSalesForceAuditTable.js